import { State } from "@neru/base/components";
import {
  namedRoute,
  useFilter,
  useProtectUnpublishedProduct,
} from "@neru/base/hooks";
import { ProductType_Enum, STATUS_TO_CONNOTATION_MAP } from "@neru/base/types";
import { getAccountId, Text } from "@neru/base/utils";
import {
  Badge,
  Breadcrumbs,
  Column,
  Icon,
  Layout,
  Phrase,
  Row,
  Spacer,
} from "@neru/ui";
import { format } from "date-fns";
import { useMemo } from "react";
import { Link } from "react-router-dom";

import { ProductBanner } from "../ProductBanner";
import {
  type FormValues,
  PRODUCT_RULES,
} from "../ProductDetails/ProductDetails";
import { useGetReleasesQuery } from "./hooks";

interface ReleasesProps {
  productId: string;
}

const Releases = ({ productId }: ReleasesProps) => {
  const [productFilter] = useFilter<FormValues>(PRODUCT_RULES, {
    deleted_at: true,
    id: productId,
  });

  const productQuery = useMemo(
    () => ({ where: productFilter }),
    [productFilter],
  );

  const { data, error, loading } = useGetReleasesQuery(productQuery);
  useProtectUnpublishedProduct(data);

  return (
    <Layout>
      <State
        emptyStateMessage={`Product with ID ${productId} not found`}
        errorMessage={error}
        isData={Boolean(data)}
        loading={loading}
      >
        {data ? (
          <>
            <Breadcrumbs>
              <Link to="/">Home</Link>
              <Link to={namedRoute("/:id", { id: productId, name: data.name })}>
                {data.name}
              </Link>
              Releases
            </Breadcrumbs>

            <ProductBanner
              className="mb-4"
              isLive={data.isLive}
              isProductOwner={getAccountId() === data?.owner}
              isPublic={data.isPublic}
              isUnlisted={data.type === ProductType_Enum.Unlisted}
            />

            {data.Releases.map((release) => (
              <Row
                className="my-4"
                data-testid={`release-${release.version}`}
                key={release.version}
              >
                <Column className="mr-6 grow basis-0 items-end justify-start">
                  <Phrase>
                    <Icon size="small" type="tag-line" />
                    <Spacer size="sm" />
                    {release.version}
                  </Phrase>
                  <Phrase className="mb-1 text-primary-500">
                    {format(new Date(release.updated_at), "dd LLL yyyy")}
                  </Phrase>
                  {release.status !== "live" && (
                    <Badge
                      connotation={STATUS_TO_CONNOTATION_MAP[release.status]}
                      data-testid={`status-version-${release.version}`}
                      layout="soft"
                    >
                      {Text.toSentenceCase(release.status, true)}
                    </Badge>
                  )}
                </Column>
                <Column
                  className={`
                    grow-[4] basis-0 justify-start border-l border-primary-200
                    px-4
                  `}
                >
                  {release.release_notes ? (
                    release.release_notes
                  ) : (
                    <Phrase className="text-primary-500">
                      No release notes.
                    </Phrase>
                  )}
                </Column>
              </Row>
            ))}
          </>
        ) : null}
      </State>
    </Layout>
  );
};

export default Releases;
